// ═══════════════════════════════════════════════════════════════════════════
// tareaLinkNotificacion — ÚNICA fuente del link a una tarea que el FRONT
// escribe en el campo `link` de una notificación
// ═══════════════════════════════════════════════════════════════════════════
//
// Espejo declarado de `functions/lib/tareaLinkNotificacion.js` — mismo nombre
// de archivo, mismo nombre de función, mismo shape de URL. Si cambia el shape,
// cambia en los dos; el test de paridad compara la SALIDA de las dos funciones
// sobre la misma tabla de inputs (`tareaLinkNotificacion.paridad.test.js`).
//
// EL BUG QUE LO ORIGINÓ (report lxBaLYCgFXIpSQuO6qDR). Los cuatro escritores de
// notificaciones de `src/core/lib/tareas.js` armaban el link a mano así:
// `/empresas/{empresaId}?tab=trabajo&id={tareaId}`. Los dos pedazos estaban mal:
//
//   · `trabajo` es el id de un GRUPO de tabs, no de un tab
//     (`src/modules/gestion/lib/tabsEmpresa.js` — los tabs reales del grupo son
//     `agenda-trabajo`, `tareas`, `ciclos`, …). `EmpresaDetailPage` no encuentra
//     contenido para ese id y normaliza al primer tab visible: el usuario
//     aterriza en la AGENDA. No en una pantalla vacía —eso se reportaría— sino
//     en el lugar equivocado, que nadie reporta y todos rodean a mano.
//   · el param `id` NO LO LEE NADIE. El lector del deep-link de tarea es
//     `AsignacionesPage` y lee `?tarea=`. `id` viajó meses sin destinatario.
//
// POR QUÉ ESTE HELPER Y NO `linkTarea()` (que es lo que sugería el reporte):
// `linkTarea` (`src/modules/asignaciones/lib/tareaLink.js`) elige el destino por
// la CAPACIDAD DEL VIEWER en runtime, y el escritor de una notificación no sabe
// quién la va a abrir — es exactamente el argumento por el que el backend
// tampoco la usa. Además su rama "desconocida" devuelve `null`, y un `link`
// PERSISTIDO no puede ser `null` a veces: se escribe una vez y se lee meses
// después. Acá se emite incondicionalmente la forma tenant-explícita, que es la
// única que no depende de cuándo se lea.
//
// ⚠️ Hasta el 19-sep-2026 esta línea decía que esa forma «sirve para todos los
// viewers» (report euhW9dN6gv8vSQhAnXOY). NO es cierto: `/asignaciones/:id`
// cuelga de `<ConsultorRoute>` (App.jsx:226) y un cliente REAL que abre la
// notificación termina en `/portal`, sin la tarea. Se emite igual porque un
// link persistido no puede ser `null` a veces y ésta es la mejor forma
// disponible hoy — no porque funcione para todos. El destino que le falta al
// portal tiene informe propio en Debugging.
//
// POR QUÉ UN ARCHIVO DE `core/` NOMBRA LA RUTA DE UN VERTICAL, si a dos archivos
// de acá `notificaciones.js` dice lo contrario. No se contradicen, y la línea
// entre los dos es la que conviene no borrar:
//   · `destinoDeNotificacion` (`notificaciones.js`) es la capa GENÉRICA de
//     navegación: repara cualquier link de cualquier feature y por eso NO puede
//     conocer rutas — si las conociera, cada vertical nuevo la tocaría.
//   · esto es el helper de SHAPE de una feature puntual, que vive en core
//     porque su llamador (`core/lib/tareas.js`) vive en core. La dependencia
//     sigue apuntando bien: el vertical importa de la horizontal, nunca al
//     revés — `tareaLink.js` (vertical) importa de acá, y acá no se importa
//     nada de `@modules/`. Un string con una ruta no es una dependencia de
//     módulo; un `import` sí lo sería, y no hay ninguno.
// Es el mismo lugar que ya eligió el backend para el mismo problema.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Link a una tarea para el campo `link` de una notificación.
 *
 * @param {string} empresaId — empresa DUEÑA de la tarea. Todo escritor la
 *   tiene: ya la escribe en el campo `empresaId` de la notificación.
 * @param {string} tareaId
 * @returns {string} ruta interna navegable. Sin `empresaId` devuelve la forma
 *   pelada: la página cae a su fallback (la empresa del viewer), que es lo que
 *   hacía siempre — no se inventa un param vacío.
 */
export function linkTareaNotificacion(empresaId, tareaId) {
  return empresaId
    ? `/asignaciones/${tareaId}?empresa=${encodeURIComponent(empresaId)}`
    : `/asignaciones/${tareaId}`
}
